import { useCallback } from 'react'

import { useAtomCallback } from 'jotai/utils'

import { CueType } from '~shared/rundown/cue'

import {
  AddRundownCuePosition,
  useAddRundownCue,
  useCreateRundownRunner,
  useDeleteRundownCue,
  useDuplicateRundownCue,
  useUpdateRundown,
  useUpdateRundownCue,
} from '@/entities/rundown'
import { useStartRunnerTimesnap, useUpdateRunner } from '@/entities/runner'
import { runnerAtom } from '@/store/runner.store'

import { getCue } from '../data/cues.store'
import { useRundownId } from '../rundown.derived'

import { getFirstCueId, getIndexedCue } from './cue.derived'
import type { AddCueCallback, UpdateCueCallback } from './menu/menu.types'
import type { TimingFixAction } from './timing/timingFixTypes'

/**
 * Id this row acts on: the cue itself, or for a group its first playable child.
 *
 * Read imperatively, so the callbacks stay independent of the rendered cue.
 */
function actionCueId(cueId: string): string | undefined {
  const cue = getCue(cueId)
  if (!cue) return undefined
  if (cue.type !== CueType.GROUP) return cue.id

  return getIndexedCue(cue.id)?.children?.find(
    (child) => getCue(child.id)?.type === CueType.CUE,
  )?.id
}

/**
 * Every mutation one cue row can trigger, with stable callbacks.
 *
 * @remarks
 *   Kept out of `CueItem` so the row passes the same function identities to its
 *   memoised children on every render, and so the eight mutation observers live
 *   in one place. The runner is read at call time (`useAtomCallback`), which
 *   keeps the callbacks stable and adds no subscriptions to the row.
 *   Only `mutate` from TanStack Query, `cueId` and `rundownId` enter the
 *   dependency arrays: all are stable per row, so a row re-render does not
 *   change any of these identities.
 *
 * @todo Migrate to valtio actions once runner has been migrated to proxy
 */
export function useCueActions(cueId: string) {
  const { mutate: updateRundown, isPending: isUpdatingRundown } =
    useUpdateRundown()
  const { mutate: createRundownRunner, isPending: isCreatingRundownRunner } =
    useCreateRundownRunner()
  const { mutate: addRundownCue, isPending: isAddingRundownCue } =
    useAddRundownCue()
  const { mutate: updateRundownCue, isPending: isUpdatingRundownCue } =
    useUpdateRundownCue()
  const { mutate: duplicateRundownCue, isPending: isDuplicatingRundownCue } =
    useDuplicateRundownCue()
  const { mutate: deleteRundownCue, isPending: isDeletingRundownCue } =
    useDeleteRundownCue()
  const { mutate: startRunnerTimesnap, isPending: isStartingRunnerTimesnap } =
    useStartRunnerTimesnap()
  const { mutate: updateRunner, isPending: isUpdatingRunner } =
    useUpdateRunner()

  const readRunner = useAtomCallback(useCallback((get) => get(runnerAtom), []))

  const rundownId = useRundownId()

  const handleSetNextCue = useCallback(() => {
    const nextCueId = actionCueId(cueId)
    if (!nextCueId) return

    const runner = readRunner()
    if (!runner) {
      updateRundown({
        rundownId,
        payload: {
          startCueId: nextCueId,
        },
      })
    } else {
      updateRunner({
        rundownId,
        runnerId: runner.id,
        payload: {
          nextCueId,
        },
      })
    }
  }, [cueId, rundownId, readRunner, updateRundown, updateRunner])

  const handleJumpToCue = useCallback(async () => {
    const jumpToCueId = actionCueId(cueId)
    if (!jumpToCueId) return

    const runner = readRunner()
    if (runner) {
      return startRunnerTimesnap({
        rundownId,
        runnerId: runner.id,
        params: {
          cueId: jumpToCueId,
          resetNextCue: true,
        },
      })
    }

    return updateRundown(
      {
        rundownId,
        payload: {
          startCueId: jumpToCueId,
        },
      },
      {
        onSuccess() {
          createRundownRunner({
            rundownId,
          })
        },
      },
    )
  }, [
    cueId,
    rundownId,
    readRunner,
    startRunnerTimesnap,
    updateRundown,
    createRundownRunner,
  ])

  const handleUpdateCue = useCallback<UpdateCueCallback>(
    (payload) => {
      updateRundownCue({
        rundownId,
        cueId,
        payload,
        params: {
          isFirstCue: getFirstCueId() === cueId,
        },
      })
    },
    [cueId, rundownId, updateRundownCue],
  )

  const handleAddCue = useCallback<AddCueCallback>(
    ({ params, payload }) => {
      addRundownCue({
        rundownId,
        params,
        payload,
      })
    },
    [rundownId, addRundownCue],
  )

  const handleDuplicateCue = useCallback(() => {
    duplicateRundownCue({
      rundownId,
      cueId,
    })
  }, [cueId, rundownId, duplicateRundownCue])

  const handleDeleteCue = useCallback(() => {
    deleteRundownCue({
      rundownId,
      cueId,
    })
  }, [cueId, rundownId, deleteRundownCue])

  /**
   * Turn a fix's write-intent (`action`, computed purely by
   * `computeTimingFixes`) into the matching mutation call.
   */
  const runFixAction = useCallback(
    (action: TimingFixAction) => {
      if (action.kind === 'patch-cue-start-time') {
        updateRundownCue({
          rundownId,
          cueId: action.cueId,
          // `startDatePlus` rides along: the engine re-derives a FIXED cue's date
          // from (time-of-day, day offset), so a move across midnight is silently
          // undone if only `startTime` is written.
          payload: {
            startTime: action.startTime,
            startDatePlus: action.startDatePlus,
          },
          params: { isFirstCue: action.isFirstCue },
        })
      } else if (action.kind === 'patch-cue-duration') {
        updateRundownCue({
          rundownId,
          cueId: action.cueId,
          payload: { duration: action.duration },
          params: { isFirstCue: action.isFirstCue },
        })
      } else {
        // Fills the gap with a new cue rather than moving an existing one, so it
        // goes through the add-cue mutation instead of a patch.
        addRundownCue({
          rundownId,
          params: { [AddRundownCuePosition.BEFORE]: action.beforeCueId },
          payload: { duration: action.duration },
        })
      }
    },
    [rundownId, updateRundownCue, addRundownCue],
  )

  const isPending =
    isUpdatingRundown ||
    isCreatingRundownRunner ||
    isAddingRundownCue ||
    isUpdatingRundownCue ||
    isDuplicatingRundownCue ||
    isDeletingRundownCue ||
    isStartingRunnerTimesnap ||
    isUpdatingRunner

  return {
    handleSetNextCue,
    handleJumpToCue,
    handleUpdateCue,
    handleAddCue,
    handleDuplicateCue,
    handleDeleteCue,
    runFixAction,
    isPending,
  }
}
